/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import toast from "react-hot-toast";
import { useForm } from "react-hook-form";
import CreatRoleModal from "./CreatRoleModal";
import {
  useRolesData,
  useOutletRolesData,
  useStaffRoleCreateMutation,
  useStaffRoleUpdateMutation,
  useStaffRoleDeleteMutation,
  useOutletRoleCreateMutation,
  useOutletRoleUpdateMutation,
  useOutletRoleDeleteMutation,
} from "../../../apis/staffApis";
import RoleAccessSection from "./RoleAccessSection";
import RoleToggle from "../../../components/RoleToggle";
import { OUTLET_ALLOWED_MODULES } from "../../../types/outletpermission";
import { usePermission } from "../../../hooks/usePermissionTabs";
import { T, useLanguage } from "../../../context/LanguageContext";

const types = ["Warehouse", "Outlet"];

export default function RoleAccessManagement() {
  const { t } = useLanguage();
  const { allowed: canCreateRole } = usePermission("access_center", "create");
  const { allowed: canUpdateRole } = usePermission("access_center", "update");
  const { allowed: canDeleteRole } = usePermission("access_center", "delete");

  const [activeRole, setActiveRole] = useState("Warehouse");
  const [selectedRole, setSelectedRole] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState<"create" | "edit" | "view">("create");
  const [role, setRole] = useState<any>(null);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // ── Warehouse hooks ──────────────────────────────────────────────────────
  const { data: warehouseRoles, refetch: fetchWarehouseRoles } = useRolesData();
  const { mutate: createWarehouse, isPending: isWarehouseCreatePending } =
    useStaffRoleCreateMutation();
  const { mutate: updateWarehouse, isPending: isWarehouseUpdatePending } =
    useStaffRoleUpdateMutation();
  const { mutate: deleteWarehouse, isPending: isWarehouseDeletePending } =
    useStaffRoleDeleteMutation();

  // ── Outlet hooks ─────────────────────────────────────────────────────────
  const { data: outletRoles, refetch: fetchOutletRoles } = useOutletRolesData();
  const { mutate: createOutlet, isPending: isOutletCreatePending } =
    useOutletRoleCreateMutation();
  const { mutate: updateOutlet, isPending: isOutletUpdatePending } =
    useOutletRoleUpdateMutation();
  const { mutate: deleteOutlet, isPending: isOutletDeletePending } =
    useOutletRoleDeleteMutation();

  // ── Active-tab derived values ─────────────────────────────────────────────
  const isOutletTab = activeRole === "Outlet";
  const fetchRoles = isOutletTab ? fetchOutletRoles : fetchWarehouseRoles;
  const isRequestPending = isOutletTab
    ? isOutletCreatePending
    : isWarehouseCreatePending;
  const isUpdateRoleRequestPending = isOutletTab
    ? isOutletUpdatePending
    : isWarehouseUpdatePending;
  const isDeleteRoleRequestPending = isOutletTab
    ? isOutletDeletePending
    : isWarehouseDeletePending;

  // ✅ undefined = show all modules (warehouse); array = filter to outlet-only modules
  const allowedModules = isOutletTab ? OUTLET_ALLOWED_MODULES : undefined;

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm();

  useEffect(() => {
    fetchWarehouseRoles();
    fetchOutletRoles();
  }, []);

  // Reset modal when switching tabs
  useEffect(() => {
    setModalOpen(false);
    setRole(null);
    setSelectedRole(null);
    setMode("create");
    reset({ roleName: "", multipleAccessWarehouse: false });
  }, [activeRole]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (activeDropdown !== null) {
        const ref = dropdownRefs.current[activeDropdown];
        if (ref && !ref.contains(event.target as Node)) setActiveDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  useEffect(() => {
    if (modalOpen) {
      reset(
        mode === "create"
          ? { roleName: "", multipleAccessWarehouse: false }
          : {
              roleName: role?.roleName || "",
              multipleAccessWarehouse: role?.multipleAccessWarehouse || false,
            },
      );
    } else {
      reset({ roleName: "", multipleAccessWarehouse: false });
    }
  }, [modalOpen, mode, role, reset]);

  // ── Modal helpers ─────────────────────────────────────────────────────────
  const openCreateModal = () => {
    setMode("create");
    setRole(null);
    reset();
    setModalOpen(true);
  };
  const openEditModal = (r: any) => {
    setMode("edit");
    setRole(r);
    setSelectedRole(r);
    reset({
      roleName: r.roleName,
      multipleAccessWarehouse: r.multipleAccessWarehouse || false,
    });
    setModalOpen(true);
  };
  const openViewModal = (r: any) => {
    setMode("view");
    setRole(r);
    setSelectedRole(r);
    reset({
      roleName: r.roleName,
      multipleAccessWarehouse: r.multipleAccessWarehouse || false,
    });
    setModalOpen(true);
  };
  const handleCloseModal = () => {
    setModalOpen(false);
    setRole(null);
    setSelectedRole(null);
    setMode("create");
    reset({ roleName: "", multipleAccessWarehouse: false });
  };

  // ── CRUD ──────────────────────────────────────────────────────────────────
  const handleRoleCreate = (data: any) => {
    const mutate = isOutletTab ? createOutlet : createWarehouse;
    mutate(
      { ...data, type: isOutletTab ? "OUTLET" : "LM" },
      {
        onSuccess: () => {
          setModalOpen(false);
          fetchRoles();
          toast.success(t("Role Added Successfully."));
        },
        onError: (error: any) =>
          toast.error(t(error?.response?.data?.message || "Something went wrong")),
      },
    );
  };

  const handleRoleUpdate = (data: any) => {
    const mutate = isOutletTab ? updateOutlet : updateWarehouse;
    mutate(
      { ...data, id: role?.id, type: isOutletTab ? "OUTLET" : "LM" },
      {
        onSuccess: () => {
          setModalOpen(false);
          fetchRoles();
          toast.success(t("Role Updated Successfully."));
        },
        onError: (error: any) =>
          toast.error(t(error?.response?.data?.message || "Something went wrong")),
      },
    );
  };

  const handleRoleDelete = (r: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (
      !window.confirm(
        t("Are you sure you want to delete the role \"{roleName}\"?").replace("{roleName}", r.roleName)
      )
    )
      return;
    const mutate = isOutletTab ? deleteOutlet : deleteWarehouse;
    mutate(r.id, {
      onSuccess: () => {
        fetchRoles();
        if (selectedRole?.id === r.id) {
          setSelectedRole(null);
          setModalOpen(false);
        }
        toast.success(t("Role deleted successfully."));
      },
      onError: (error: any) =>
        toast.error(t(error?.message || "Failed to delete role")),
    });
  };

  // ── Shared role list ──────────────────────────────────────────────────────
  const renderRoleList = (roleList: any[]) => (
    <ul className="overflow-y-auto  h-[calc(100vh-16rem)]  divide-y">
      {roleList?.map((r: any) => (
        <li
          key={r.id}
          onClick={() => openViewModal(r)}
          className={`flex items-center justify-between px-4 py-3 cursor-pointer ${selectedRole?.id === r.id ? "bg-[#FBD037]" : "hover:bg-[#FBD037]/20"}`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#fff] text-[#212121] font-medium text-base flex items-center justify-center uppercase">
              {r.roleName?.charAt(0)}
            </div>
            <span className="text-base text-[#212121] font-medium">
              {r.roleName}
            </span>
          </div>
          <div className="relative space-x-2">
            {(!r.permissions || r.permissions.length === 0) && canDeleteRole && (
              <button
                onClick={(e) => handleRoleDelete(r, e)}
                disabled={isDeleteRoleRequestPending}
                className="p-2 border rounded-full bg-[#F7F6F2] hover:bg-red-50 hover:border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                title={t("Delete Role")}
              >
                <IconTrash
                  strokeWidth={1.5}
                  size={20}
                  className="text-red-500"
                />
              </button>
            )}
            {canUpdateRole && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openEditModal(r);
                }}
                className="p-2 border rounded-full bg-[#F7F6F2]"
                title={t("Edit Role")}
              >
                <IconEdit strokeWidth={1.5} size={20} />
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="grid grid-cols-12  bg-white h-full">
      {/* Left Sidebar */}
      <div className="col-span-12 lg:col-span-4 xl:col-span-4  overflow-hidden overflow-y-auto  mb-4 lg:mb-0">
        <div className="flex justify-between items-center py-2 px-4 border-b border-[#000000]/10 sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <h2 className="font-medium text-[#212121] text-lg"><T>Role</T></h2>
            <RoleToggle
              options={types}
              active={activeRole}
              onChange={setActiveRole}
            />
          </div>
          {canCreateRole && (
            <button
              onClick={openCreateModal}
              className="flex items-center gap-1 p-2.5 text-sm rounded-full bg-[#FBD037] hover:bg-[#212121] hover:text-white text-[#212121] transition"
            >
              <IconPlus stroke={1.5} size={22} />
            </button>
          )}
        </div>
        {!isOutletTab && renderRoleList(warehouseRoles?.roles || [])}
        {isOutletTab && renderRoleList(outletRoles?.roles || [])}
      </div>

      <div className="col-span-12 lg:col-span-8 xl:col-span-8 h-full border-l border-[#000000]/10">
        {modalOpen ? (
          <div className="divide-y divide-[#000000]/10">
            <RoleAccessSection
              title={
                mode === "create"
                  ? "Create New Role"
                  : mode === "edit"
                    ? "Edit Role"
                    : "View Role"
              }
              open={modalOpen}
              onClose={handleCloseModal}
              actionButtonText={
                mode === "view"
                  ? false
                  : mode === "create"
                    ? t("Create")
                    : t("Update")
              }
              actionButtonOnClick={
                mode === "create"
                  ? handleSubmit(handleRoleCreate)
                  : handleSubmit(handleRoleUpdate)
              }
              actionButtonProps={{
                disabled: isRequestPending || isUpdateRoleRequestPending,
              }}
              key={activeRole + mode + (role?.id || "new")}
              register={register}
              errors={errors}
              setValue={setValue}
              defaultValues={role}
              readOnly={mode === "view"}
              closeButtonText="Cancel"
              showFooter
              hideMultipleAccess={isOutletTab}
            >
              <CreatRoleModal
                key={activeRole + mode + (role?.id || "new")}
                register={register}
                errors={errors}
                setValue={setValue}
                defaultValues={role}
                readOnly={mode === "view"}
                allowedModules={allowedModules}
              />
            </RoleAccessSection>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="p-8 flex flex-col items-center space-y-4">
              <img src="/img/scan-data.png" alt="scan prompt" />
              <div className="text-center">
                <h2 className="text-[#212121] text-lg font-medium">
                  <T>Please Select Role</T>
                </h2>
                <p className="text-[#212121]/70 text-sm">
                  <T>You don't have any Role yet.</T>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
